import { DeepReadonly } from 'vue'

import { SPECIAL_ANSWER_FIELD_IDS } from '../../answer/constants'

export type FieldType =
  | 'text'
  | 'video'
  | 'choice'
  | 'imageChoice'
  | 'choiceText'
  | 'nps'
  | 'ranked'
  | 'special'
  | 'maxDiffSetChoice'
  | 'slider'

export const FIELD_SPECIAL_TYPE = [
  SPECIAL_ANSWER_FIELD_IDS.SKIP,
  SPECIAL_ANSWER_FIELD_IDS.NA,
  SPECIAL_ANSWER_FIELD_IDS.NONE,
] as const
export type FieldSpecialType = (typeof FIELD_SPECIAL_TYPE)[number]
export const isSpecialFieldId = (id: string): id is FieldSpecialType =>
  (FIELD_SPECIAL_TYPE as readonly string[]).includes(id)

export type Field<T extends FieldType = FieldType> = Readonly<
  {
    text: FieldStructure<'text'>
    video: FieldStructure<'video'>
    choice: FieldStructure<'choice'>
    imageChoice: FieldStructure<'imageChoice'>
    choiceText: FieldStructure<'choiceText'>
    nps: FieldStructure<'nps'>
    ranked: FieldStructure<'ranked'>
    special: FieldStructure<'special', FieldSpecialType>
    maxDiffSetChoice: FieldStructure<'maxDiffSetChoice'>
    slider: FieldStructure<'slider'>
  }[T]
>

type FieldStructure<T extends FieldType, Id extends string = string> = {
  id: Id
  type: T
} & FieldExtra<T>

type FieldExtra<T extends FieldType = FieldType> = {
  text: { title: string }
  choice: { title: string }
  imageChoice: { description: string | undefined; url: string }
  video: unknown
  choiceText: unknown
  nps: {
    ratingToApiAnswerIdMap: Map<number, string>
  }
  ranked: { title: string }
  special: unknown
  maxDiffSetChoice: { title: string; setId: string; answerId: string }
  // No ratingToApiAnswerIdMap equivalent: a slider answer is submitted as a number, not
  // resolved back to a choice id the way NPS is.
  slider: unknown
}[T]

export type FieldSpecial<T extends FieldSpecialType = FieldSpecialType> = DeepReadonly<
  FieldStructure<'special', T>
>

export function assertFieldType<T extends FieldType>(
  type: unknown,
  expected: T,
): asserts type is T {
  if (type !== expected) throw TypeError(`Expected question type ${expected}, but recieved ${type}`)
}
